import { useEffect, useState } from "react";
import { Icon } from "../components/Icon";
import { Avatar } from "../shell/Avatar";
import { OwnerEmpty } from "../shell/OwnerEmpty";
import { PageHead } from "../shell/PageHead";
import { useDraft } from "../lib/drafts";
import type { PanelEntry } from "../lib/api";
import type { SurfacePages } from "../lib/surfaceRequests";
import { panelHref, workspaceEntries, workspaceTitle } from "../lib/workspaces";
import { agentHref, hubSettingsHref, instanceStatus, readAgentView, type AgentInstance } from "./api";
import "./agent-catalog.css";

/** The search box earns its room only when there are more IPs than fit in one look. */
const SEARCH_FROM = 5;

export function AgentCatalog({ agents, stale, accountId = "local" }: {
  agents: AgentInstance[]; stale: boolean; accountId?: string;
}) {
  const [query, setQuery] = useState("");
  const [selected, select] = useDraft<string[]>(`hub:parallel-selection:${accountId}`, [], "/");
  const chosen = agents.filter(agent => selected.includes(agent.id));
  const shown = agents.filter(agent => agent.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()));
  const parallel = () => {
    const params = new URLSearchParams();
    for (const agent of chosen) params.append("agent", agent.id);
    location.hash = "#/parallel?" + params;
  };
  return <div className="owner-page agent-catalog">
    <PageHead title="我的 IP" meta={agents.length ? `${agents.length} 个 IP` : undefined} actions={<>
      {agents.length > 1 ? <button className="btn" type="button" onClick={parallel} disabled={chosen.length < 2 || stale}><Icon name="wall" size={16} />并排工作</button> : null}
      {agents.length ? <a className="btn btn-accent" href="/#/create"><Icon name="plus" size={16} />新建 IP</a> : null}
    </>} />
    <p>每个 IP 都保留自己的对话、记忆和作品，也能处理多个方向的独立任务。用同一个 IP 还是分开合作，由你按需要安排。</p>
    {!agents.length ? <OwnerEmpty icon="agent" action={<a className="btn btn-accent" href="/#/create"><Icon name="plus" size={16} />新建 IP</a>}
      quiet={<>想用自己的电脑？<a href="/#/clients">连接客户端</a></>}>带来一个经验或判断，和它一起找到内容方向。</OwnerEmpty>
    : <>
      {agents.length > 1 ? <p>选择2个或以上 IP，同时查看各自工作，对话互不影响。</p> : null}
      {agents.length > SEARCH_FROM ? <div className="agent-catalog-tools">
        <label className="agent-catalog-search"><Icon name="look" size={16} /><input className="text-input" aria-label="搜索 IP 名称"
          placeholder="按名称找 IP" value={query} onChange={event => setQuery(event.target.value)} /></label>
        {chosen.length ? <p>已选择 {chosen.length} 个</p> : null}
      </div> : chosen.length ? <p className="agent-catalog-chosen">已选择 {chosen.length} 个</p> : null}
      <section className="agent-catalog-grid" aria-label="你的 IP 列表">
        {shown.map(agent => <AgentCard key={agent.id} agent={agent} stale={stale} selected={selected.includes(agent.id)} canSelect={agents.length > 1}
          onSelect={() => select(selected.includes(agent.id) ? selected.filter(id => id !== agent.id) : [...selected, agent.id])} />)}
      </section>
      {!shown.length ? <OwnerEmpty icon="search" action={<button type="button" className="btn" onClick={() => setQuery("")}>清除搜索</button>}>没有找到这个名称，换个词再找。</OwnerEmpty> : null}
    </>}
  </div>;
}

function AgentCard({ agent, stale, selected, canSelect, onSelect }: {
  agent: AgentInstance; stale: boolean; selected: boolean; canSelect: boolean; onSelect: () => void;
}) {
  const [pages, setPages] = useState<PanelEntry[] | null>(null);
  const [readError, setReadError] = useState(false);
  useEffect(() => {
    if (!agent.online || stale) return;
    const controller = new AbortController();
    readAgentView<SurfacePages>(agent.id, "/api/surface-pages", controller.signal, true).then(result => {
      if (controller.signal.aborted) return;
      if (!Array.isArray(result.pages)) throw new Error("工作界面未读完整");
      setPages(workspaceEntries(result.pages)); setReadError(false);
    }).catch(() => { if (!controller.signal.aborted) setReadError(true); });
    return () => controller.abort();
  }, [agent.id, agent.online, stale]);
  const status = instanceStatus(agent);
  const primary = pages?.find(page => page.workspace?.role === "entry" || page.workspace?.role === "template") ?? pages?.[0];
  const where = agent.location === "cloud" ? "云端托管" : agent.client ? "自己的电脑 · " + agent.client.name : "自己的电脑";
  return <article className="owner-card catalog-card" data-state={agent.online ? "online" : "offline"}>
    <a className="catalog-identity" href={agentHref(agent.id)}>
      <Avatar name={agent.name} identity={agent.id} size="lg" />
      <span className="catalog-name"><h2 data-quoted="true">{agent.name}</h2>
        <small><i data-tone={stale ? "neutral" : status.tone} />{stale ? "状态待更新" : status.label} · {where}</small></span>
    </a>
    {canSelect ? <label className="catalog-compare"><input type="checkbox" checked={selected} onChange={onSelect}
      aria-label={`选择 ${agent.name} 用于并排`} /><span className="visually-hidden">用于并排</span></label> : null}
    <p className="catalog-work">
      {agent.online && !stale ? readError ? "工作界面暂未读到"
          : pages === null ? <span role="status">正在读取它的工作界面…</span>
            : primary ? workspaceTitle(primary) : "还没有工作界面" : " "}
    </p>
    <div className="catalog-actions">
      {agent.online && !stale ? <a className="btn" href={agentHref(agent.id, primary ? panelHref(primary.key) : "#/today")}>进入<Icon name="arrow" size={16} /></a>
        : <a className="btn" href={agentHref(agent.id, "#/today")}>打开<Icon name="arrow" size={16} /></a>}
      <a className="btn btn-quiet btn-icon" aria-label={`设置 ${agent.name}`} title="设置" href={hubSettingsHref(agent.id)}><Icon name="settings" size={18} /></a>
    </div>
  </article>;
}
